import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  LuBuilding2,
  LuCamera,
  LuChevronDown,
  LuFolderKanban,
  LuHouse,
  LuKeyRound,
  LuLoaderCircle,
  LuMail,
  LuMessagesSquare,
  LuPencil,
  LuPhone,
  LuPhoneCall,
  LuPlus,
  LuStar,
  LuBookOpenCheck,
} from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import Popover from '../../../components/ui/Popover';
import { getInitials } from '../../../components/ui/Avatar';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { useTranslation } from '../../../i18n/LanguageContext';
import { PRIMARY_ADDRESS_HOME, PRIMARY_ADDRESS_WORK, isPrimaryAddress } from '../../../utils/employeeAddress';
import PhotoUploadDialog, { prepareProfilePhoto } from './PhotoUploadDialog';
import { CARD, ProgressBar } from './ProfileUi';

const greetingKey = (hour) => {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'night';
};

/** Ring of the profile completion percentage. */
const CompletionRing = ({ percent, size = 44 }) => {
  const radius = 15.9155;
  const tone = percent >= 100 ? 'tw-stroke-emerald-500' : percent >= 60 ? 'tw-stroke-teal-500' : 'tw-stroke-amber-500';
  return (
    <svg viewBox="0 0 36 36" width={size} height={size} aria-hidden="true" className="tw-shrink-0 -tw-rotate-90">
      <circle cx="18" cy="18" r={radius} fill="none" strokeWidth="3.5" className="tw-stroke-slate-200 dark:tw-stroke-white/10" />
      <circle cx="18" cy="18" r={radius} fill="none" strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${percent}, 100`} className={`tw-transition-[stroke-dasharray] tw-duration-700 ${tone}`} />
    </svg>
  );
};

CompletionRing.propTypes = { percent: PropTypes.number.isRequired, size: PropTypes.number };

/** One contact detail with an optional edit button. */
const ContactItem = ({ icon: Icon, label, value, emptyText, badge, onEdit, editLabel, addLabel }) => (
  <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/60 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
    <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-slate-500/10 tw-text-slate-500 dark:tw-text-slate-300">
      <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
    </span>
    <div className="tw-min-w-0 tw-flex-1">
      <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">
        {label}
        {badge}
      </p>
      <p className={`tw-mt-0.5 tw-break-words tw-text-sm ${value ? 'tw-font-medium tw-text-slate-800 dark:tw-text-slate-100' : 'tw-italic tw-text-slate-400 dark:tw-text-slate-500'}`}>
        {value || emptyText}
      </p>
    </div>
    {onEdit && (
      <button
        type="button"
        onClick={onEdit}
        aria-label={value ? editLabel : addLabel}
        title={value ? editLabel : addLabel}
        className="tw-inline-flex tw-h-8 tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2.5 tw-text-xs tw-font-semibold tw-text-teal-700 tw-transition-colors hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
      >
        {value ? <LuPencil aria-hidden="true" className="tw-h-3.5 tw-w-3.5" /> : <LuPlus aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
        <span className="max-sm:tw-sr-only">{value ? editLabel : addLabel}</span>
      </button>
    )}
  </div>
);

ContactItem.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  emptyText: PropTypes.string.isRequired,
  badge: PropTypes.node,
  onEdit: PropTypes.func,
  editLabel: PropTypes.string,
  addLabel: PropTypes.string,
};

/**
 * Top of the profile: photo (changeable), greeting, name and role, quick
 * figures that jump to their tab, profile completion with what's missing,
 * password change and the contact details (address and emergency phone are
 * editable here).
 */
const ProfileHero = ({
  userData,
  loading,
  profileImageUrl,
  onUploadPhoto,
  onPhotoError,
  completion,
  stats,
  onOpenTab,
  onEditAddress,
  onEditPhone,
  onChangePassword,
}) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.hero.${key}`, vars);
  const isWide = useMediaQuery('(min-width: 768px)');
  const [contactsOpen, setContactsOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  // Yüklenemeyen fotoğraf için önce güncel adres istenir, sonra bir kez daha denenir
  const [photoRetrying, setPhotoRetrying] = useState(false);
  const [photoAttempt, setPhotoAttempt] = useState(0);
  const photoRetried = useRef(false);
  // Seçilen fotoğraf önce önizlenir; "Kaydet" denince yüklenir
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const completionRef = useRef(null);

  const fullName = `${userData.eName} ${userData.eSurname}`.trim();
  const now = new Date();

  // Adres değişince (ör. güncel indirme adresi geldiğinde) fotoğraf yeniden denenir
  useEffect(() => {
    setPhotoFailed(false);
    setPhotoRetrying(false);
    photoRetried.current = false;
  }, [profileImageUrl]);

  const handlePhotoError = async () => {
    if (photoRetried.current) {
      setPhotoFailed(true);
      return;
    }
    photoRetried.current = true;
    setPhotoRetrying(true);
    const failedUrl = profileImageUrl;
    const replaced = onPhotoError ? await onPhotoError(failedUrl) : false;
    if (!replaced) {
      // Geçici bir ağ hatası olabilir: aynı adres bir kez daha yüklenir
      setPhotoAttempt((attempt) => attempt + 1);
      setPhotoRetrying(false);
    }
  };

  const pickPhoto = () => fileInputRef.current?.click();
  const handlePhoto = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (file) setPendingPhoto(file);
  };

  const savePhoto = async (file) => {
    setUploading(true);
    const prepared = await prepareProfilePhoto(file);
    const saved = await onUploadPhoto(prepared);
    setUploading(false);
    if (saved) {
      setPhotoFailed(false);
      setPendingPhoto(null);
    }
  };

  const quickStats = [
    { key: 'projects', icon: LuFolderKanban, value: stats.activeProjects, tab: 'projelerim' },
    { key: 'sops', icon: LuBookOpenCheck, value: stats.sopTotal ? `${stats.sopRead}/${stats.sopTotal}` : '0', tab: 'dosyalar' },
    { key: 'messages', icon: LuMessagesSquare, value: stats.unreadMessages, tab: 'mesajlar', alert: stats.unreadMessages > 0 },
  ];

  const missingPersonal = completion.personal.missing;
  const suggestions = [
    completion.photo.percent < 100 && { key: 'photo', onClick: pickPhoto },
    missingPersonal.includes('emergencyPhone') && { key: 'emergencyPhone', onClick: onEditPhone },
    missingPersonal.includes('address') && { key: 'address', onClick: onEditAddress },
    completion.education.percent < 100 && { key: 'education', onClick: () => onOpenTab('dosyalar') },
  ].filter(Boolean);
  const othersMissing = missingPersonal.some((field) => !['emergencyPhone', 'address'].includes(field));

  const completionRows = [
    { key: 'personal', percent: completion.personal.percent, detail: label('completion.personalDetail', { filled: completion.personal.filled, total: completion.personal.total }) },
    { key: 'photo', percent: completion.photo.percent, detail: completion.photo.percent === 100 ? label('completion.done') : label('completion.missing') },
    {
      key: 'education',
      percent: completion.education.percent,
      detail: completion.education.total === 0 ? label('completion.noAssignments') : label('completion.educationDetail', { completed: completion.education.completed, total: completion.education.total }),
    },
  ];

  const completionButton = (compact) => (
    <button
      ref={completionRef}
      type="button"
      onClick={() => setCompletionOpen(true)}
      aria-expanded={completionOpen}
      className={`tw-flex tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-text-left tw-transition-colors hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:hover:tw-bg-white/[0.08] ${compact ? 'tw-py-1.5 tw-pl-1.5 tw-pr-3' : 'tw-py-2 tw-pl-2 tw-pr-3.5'}`}
    >
      <span className="tw-relative tw-grid tw-place-items-center">
        <CompletionRing percent={completion.overall} size={compact ? 36 : 44} />
        <span className="tw-absolute tw-text-[10px] tw-font-bold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{completion.overall}</span>
      </span>
      <span className="tw-min-w-0">
        <span className="tw-block tw-text-xs tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{label('completion.title')}</span>
        <span className="tw-block tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">{completion.overall >= 100 ? label('completion.complete') : label('completion.seeMissing')}</span>
      </span>
    </button>
  );

  const contacts = (
    <div className="tw-grid tw-gap-2.5 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
      <ContactItem icon={LuPhone} label={label('contacts.phone')} value={userData.phone} emptyText={label('contacts.none')} />
      <ContactItem
        icon={LuPhoneCall}
        label={label('contacts.emergency')}
        value={userData.emergencyPhone}
        emptyText={label('contacts.notAdded')}
        onEdit={onEditPhone}
        editLabel={label('contacts.edit')}
        addLabel={label('contacts.add')}
      />
      <ContactItem icon={LuMail} label={label('contacts.email')} value={userData.eMail} emptyText={label('contacts.none')} />
      <ContactItem
        icon={LuHouse}
        label={label('contacts.home')}
        value={userData.address}
        emptyText={label('contacts.notAdded')}
        badge={userData.address && isPrimaryAddress(userData, PRIMARY_ADDRESS_HOME) ? <PrimaryBadge text={label('contacts.primary')} /> : null}
        onEdit={onEditAddress}
        editLabel={label('contacts.edit')}
        addLabel={label('contacts.add')}
      />
      <ContactItem
        icon={LuBuilding2}
        label={label('contacts.work')}
        value={userData.workAddress}
        emptyText={label('contacts.notAdded')}
        badge={isPrimaryAddress(userData, PRIMARY_ADDRESS_WORK) ? <PrimaryBadge text={label('contacts.primary')} /> : null}
        onEdit={onEditAddress}
        editLabel={label('contacts.edit')}
        addLabel={label('contacts.add')}
      />
    </div>
  );

  return (
    <section aria-label={label('label')} className={`${CARD} tw-isolate tw-overflow-hidden tw-p-4 sm:tw-p-6`}>
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-left-24 -tw-top-28 -tw-z-10 tw-h-64 tw-w-64 tw-rounded-full tw-bg-teal-300/40 tw-blur-3xl dark:tw-bg-teal-500/15" />
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-right-16 -tw-top-24 -tw-z-10 tw-h-56 tw-w-56 tw-rounded-full tw-bg-sky-300/30 tw-blur-3xl dark:tw-bg-sky-500/10" />

      <div className="tw-flex tw-flex-col tw-gap-4 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-4">
          {/* Fotoğraf */}
          <div className="tw-relative tw-shrink-0">
            {profileImageUrl && !photoFailed && !photoRetrying ? (
              <img
                key={`${profileImageUrl}-${photoAttempt}`}
                src={profileImageUrl}
                alt={fullName || label('photoAlt')}
                onError={handlePhotoError}
                className="tw-h-16 tw-w-16 tw-rounded-[1.4rem] tw-object-cover tw-shadow-lg tw-ring-2 tw-ring-white/90 sm:tw-h-20 sm:tw-w-20 dark:tw-ring-white/15"
              />
            ) : (
              <span className="tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-[1.4rem] tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-xl tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30 sm:tw-h-20 sm:tw-w-20 sm:tw-text-2xl">
                {loading ? '' : getInitials(userData.eName, userData.eSurname)}
              </span>
            )}
            <button
              type="button"
              onClick={pickPhoto}
              disabled={uploading}
              aria-label={label('changePhoto')}
              title={label('changePhoto')}
              className="tw-absolute -tw-bottom-1.5 -tw-right-1.5 tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-border-2 tw-border-white tw-bg-slate-900 tw-text-white tw-shadow-md tw-transition-transform hover:tw-scale-105 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 disabled:tw-cursor-wait dark:tw-border-[#22262f] dark:tw-bg-white dark:tw-text-slate-900"
            >
              {uploading ? <LuLoaderCircle aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-animate-spin" /> : <LuCamera aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhoto} className="tw-hidden" />
          </div>

          <div className="tw-min-w-0">
            <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t(`common.greeting.${greetingKey(now.getHours())}`)},</p>
            {loading ? (
              <span className="tw-mt-1 tw-block tw-h-7 tw-w-48 tw-animate-pulse tw-rounded-lg tw-bg-slate-200/80 dark:tw-bg-white/10" />
            ) : (
              <h1 className="tw-truncate tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 sm:tw-text-2xl lg:tw-text-[1.75rem] dark:tw-text-white">{fullName || label('noName')}</h1>
            )}
            <p className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
              {userData.role && <span className="tw-rounded-full tw-bg-teal-500/10 tw-px-2.5 tw-py-0.5 tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">{userData.role}</span>}
              <span className="tw-capitalize">{now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </p>
          </div>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {completionButton(!isWide)}
          <Button size={isWide ? 'md' : 'sm'} onClick={onChangePassword}>
            <LuKeyRound aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('changePassword')}
          </Button>
        </div>
      </div>

      {/* Hızlı bakış: dokununca ilgili sekme açılır */}
      <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-2">
        {quickStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.key}
              type="button"
              onClick={() => onOpenTab(stat.tab)}
              className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/60 tw-p-2.5 tw-text-left tw-transition-colors hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 sm:tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.07]"
            >
              <span className={`tw-hidden tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl sm:tw-grid ${stat.alert ? 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' : 'tw-bg-teal-500/10 tw-text-teal-700 dark:tw-text-teal-300'}`}>
                <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
              </span>
              <span className="tw-min-w-0">
                <span className={`tw-block tw-text-lg tw-font-semibold tw-leading-none tw-tabular-nums ${stat.alert ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-slate-900 dark:tw-text-white'}`}>{stat.value}</span>
                <span className="tw-mt-1 tw-block tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label(`stats.${stat.key}`)}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* İletişim bilgileri: telefonda açılıp kapanır */}
      <div className="tw-mt-4">
        {!isWide && (
          <button
            type="button"
            onClick={() => setContactsOpen(!contactsOpen)}
            aria-expanded={contactsOpen}
            className="tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-2xl tw-bg-slate-500/[0.06] tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-slate-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-bg-white/[0.04] dark:tw-text-slate-200"
          >
            {label('contacts.title')}
            <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${contactsOpen ? 'tw-rotate-180' : ''}`} />
          </button>
        )}
        {isWide && <p className="tw-mb-2 tw-px-0.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{label('contacts.title')}</p>}
        {(isWide || contactsOpen) && <div className={isWide ? '' : 'tw-mt-2.5'}>{contacts}</div>}
      </div>

      <PhotoUploadDialog
        file={pendingPhoto}
        saving={uploading}
        onCancel={() => setPendingPhoto(null)}
        onConfirm={savePhoto}
        onPickAnother={pickPhoto}
      />

      <Popover anchorRef={completionRef} open={completionOpen} onClose={() => setCompletionOpen(false)} align="end" label={label('completion.title')} className="tw-w-[min(22rem,calc(100vw-1.5rem))] tw-p-4">
        <div className="tw-space-y-4">
          <div className="tw-flex tw-items-center tw-gap-3">
            <span className="tw-relative tw-grid tw-place-items-center">
              <CompletionRing percent={completion.overall} size={52} />
              <span className="tw-absolute tw-text-xs tw-font-bold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{completion.overall}%</span>
            </span>
            <div className="tw-min-w-0">
              <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('completion.title')}</p>
              <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('completion.explain')}</p>
            </div>
          </div>
          <ul className="tw-space-y-3">
            {completionRows.map((row) => (
              <li key={row.key} className="tw-space-y-1">
                <p className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-xs">
                  <span className="tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label(`completion.rows.${row.key}`)}</span>
                  <span className="tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{row.percent}%</span>
                </p>
                <ProgressBar percent={row.percent} tone={row.percent >= 100 ? 'tw-bg-emerald-500' : 'tw-bg-teal-500'} />
                <p className="tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">{row.detail}</p>
              </li>
            ))}
          </ul>
          {suggestions.length > 0 && (
            <div className="tw-space-y-1.5">
              <p className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('completion.todo')}</p>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.key}
                  type="button"
                  onClick={() => {
                    setCompletionOpen(false);
                    suggestion.onClick();
                  }}
                  className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-xl tw-bg-teal-500/[0.07] tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-text-teal-800 tw-transition-colors hover:tw-bg-teal-500/15 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-200"
                >
                  <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
                  {label(`completion.suggestions.${suggestion.key}`)}
                </button>
              ))}
            </div>
          )}
          {othersMissing && <p className="tw-text-[11px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label('completion.othersMissing')}</p>}
        </div>
      </Popover>
    </section>
  );
};

const PrimaryBadge = ({ text }) => (
  <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">
    <LuStar aria-hidden="true" className="tw-h-2.5 tw-w-2.5 tw-fill-amber-400 tw-text-amber-500" />
    {text}
  </span>
);

PrimaryBadge.propTypes = { text: PropTypes.string.isRequired };

ProfileHero.propTypes = {
  userData: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  profileImageUrl: PropTypes.string,
  onUploadPhoto: PropTypes.func.isRequired,
  onPhotoError: PropTypes.func,
  completion: PropTypes.object.isRequired,
  stats: PropTypes.shape({
    activeProjects: PropTypes.number,
    sopRead: PropTypes.number,
    sopTotal: PropTypes.number,
    unreadMessages: PropTypes.number,
  }).isRequired,
  onOpenTab: PropTypes.func.isRequired,
  onEditAddress: PropTypes.func.isRequired,
  onEditPhone: PropTypes.func.isRequired,
  onChangePassword: PropTypes.func.isRequired,
};

export default ProfileHero;
